// ============================================================
// App.tsx
// Root component — shows login, settings, or app based on state
// ============================================================
import { flushAllAutoSaves } from './hooks/flushAllAutoSaves'
import { confirmLeave } from './navigation/unsavedChanges'
import { pausePlaceSync, startPlaceSync } from './navigation/navigate'
import { placeFromSearch, placeToSearch, type Place } from './navigation/placeUrl'
import type { CaseSectionKey } from './types/case.types'
import UnsavedChangesDialog from './components/shared/UnsavedChangesDialog'
import { getTimerNavGuard } from './utils/timerNavGuard'
import React, { useEffect, useRef, useState } from 'react'
import ViewingPane from './components/layout/ViewingPane'
import LoginScreen from './components/auth/LoginScreen'
import LoadingSplash from './components/shared/LoadingSplash'
import { usePresenceConnection, useFieldFocusDelegation, useClientFieldEditSync, useEntityChangeSync, useFieldCaretSync, useRemoteCaretTextApply } from './hooks/usePresence'
import RegisterScreen from './components/auth/RegisterScreen'
import RequestResetScreen from './components/auth/RequestResetScreen'
import ConfirmationScreen from './components/auth/ConfirmationScreen'
import AcceptResetScreen from './components/auth/AcceptResetScreen'
import AcceptInviteScreen from './components/auth/AcceptInviteScreen'
import ForcedTwoFactorSetup from './components/auth/ForcedTwoFactorSetup'
import Sidebar from './components/layout/Sidebar'
import { HeaderPortalContext } from './components/layout/HeaderPortalContext'
import { usePersistedToggle } from './hooks/usePersistedToggle'
import FavouritesErrorToast from './components/shared/FavouritesErrorToast'
import SaveConflictToast from './components/shared/SaveConflictToast'
import ScreenZoom from './components/shared/ScreenZoom'
import AlertsStatusBanner from './components/shared/AlertsStatusBanner'
import Settings from './pages/Settings'
import AdminPage from './pages/AdminPage'
import ClientSearchView from './pages/ClientSearchView'
import CalendarView from './pages/CalendarView'
import DocumentSearchView from './pages/DocumentSearchView'
import BillingView from './pages/BillingView'
import TasksView from './pages/TasksView'
import DashboardView from './pages/DashboardView'
import { useAutoSave } from './hooks/useAutoSave'
import { useClientAutoSave } from './hooks/useClientAutoSave'
import { useCasevoStore } from './store/useCasevoStore'
import { useShallow } from 'zustand/react/shallow'
import { getMeApi, getCaseHistoryVisibilityApi } from './api/casevoApi'
import { presence } from './services/presence'
import { startThemeController } from './utils/theme'
import { useLiveTopics } from './hooks/useLiveTopics'
import { requestStatementFocus } from './components/settlement/statement/statementFocus'
const App: React.FC = () => {
  // ★ (Audit, perf) Shallow-selected — this ROOT component used to subscribe to
  // the WHOLE store, so every store write (each keystroke's updateCase, presence,
  // toasts, save state) re-rendered App and the entire tree beneath it. Now it
  // re-renders only when one of these values changes.
  const {
    isAuthenticated,
    showDashboard,
    showSettings,
    showAdmin,
    showClientSearch,
    showCalendar,
    showDocumentSearch,
    showBilling,
    showTasks,
    taskOpenedFrom,
    authUser,
    setAuthUser,
    authView,
    setAuthView,
    pendingResetToken,
    setPendingResetToken,
    pendingInviteToken,
    setPendingInviteToken,
    pendingCaseId,
    setPendingCaseId,
    loadColourSchemes,
    loadFavourites,
  } = useCasevoStore(useShallow((s) => ({
    isAuthenticated: s.isAuthenticated,
    showDashboard: s.showDashboard,
    showSettings: s.showSettings,
    showAdmin: s.showAdmin,
    showClientSearch: s.showClientSearch,
    showCalendar: s.showCalendar,
    showDocumentSearch: s.showDocumentSearch,
    showBilling: s.showBilling,
    showTasks: s.showTasks,
    taskOpenedFrom: s.taskOpenedFrom,
    authUser: s.authUser,
    setAuthUser: s.setAuthUser,
    authView: s.authView,
    setAuthView: s.setAuthView,
    pendingResetToken: s.pendingResetToken,
    setPendingResetToken: s.setPendingResetToken,
    pendingInviteToken: s.pendingInviteToken,
    setPendingInviteToken: s.setPendingInviteToken,
    pendingCaseId: s.pendingCaseId,
    setPendingCaseId: s.setPendingCaseId,
    loadColourSchemes: s.loadColourSchemes,
    loadFavourites: s.loadFavourites,
  })))
  // True only while the ?caseId "open in new tab" cold-open fetch is in
  // flight. Local to App (not the store) — nothing else needs it, and normal
  // in-app navigation never sets it, so this ONLY affects the cold-open path.
  // renderMain shows a spinner while true, avoiding the case-list flash between
  // "showDashboard cleared" and "currentCase resolved".
  const [openingCaseById, setOpeningCaseById] = useState(false)
  // Records a ?taskId cold-open captured at mount so the consume effect
  // below navigates to the Tasks view ONLY for the URL deep-link (mirrors
  // the ?caseId cold-open — e.g. an email "View task" CTA). In-app
  // producers (ClientSearchView, CalendarView) also set pendingTaskId but
  // navigate themselves, so they leave this ref null and keep owning their
  // own navigation.
  const coldOpenTaskIdRef = useRef<string | null>(null)
  // Records a ?day=YYYY-MM-DD cold-open (the reminder email CTA) so the
  // consume effect below opens the Calendar to that day ONLY for the URL
  // deep-link. CalendarView reads pendingCalendarDate to anchor + open the
  // day panel.
  const coldOpenDayRef = useRef<string | null>(null)
  // Any other place named by the URL (navigation/placeUrl) — a refresh on a
  // client page, Billing with a bill open, an Admin section…
  const coldOpenPlaceRef = useRef<Place | null>(null)
  // Real-time case-level presence — connects on login, announces the open
  // case, exposes viewers to ViewingPaneHeader. Additive + degrades
  // gracefully (no-op when the socket is down).
  usePresenceConnection()
  // Field-level presence focus delegation — mounted app-wide (was in
  // ViewingPane, which only covers the case surface). One document-level
  // focusin listener catches field focus on EVERY surface (case, client,
  // task), so field presence works wherever a field has a data-field.
  useFieldFocusDelegation()
  // Stage 1a on-blur live edit-sync (CLIENT DETAILS only). One document-level
  // focusout sender + one field-edit receiver, app-wide so both client-details
  // surfaces (ClientSearchView live client + case clientDetails snapshot) are
  // covered. Additive + degrades gracefully — no-op when the socket is down.
  useClientFieldEditSync()
  // Stage A live form-field cursors (PILOT): one document-level, throttled caret
  // sender for the single pilot text field. Additive + best-effort — no-op when
  // the socket is down or the pilot field isn't focused.
  useFieldCaretSync()
  // Stage A: apply the caret message's full value immediately so the remote TEXT
  // updates in lock-step with the remote cursor (pilot field). Near-live sync is
  // the persistence backstop; guarded against clobbering the local user's typing.
  useRemoteCaretTextApply()
  // Live-freshness: firm-wide server-driven entity changes (Stages 2+3) —
  // UPDATE patches every store-backed list row (+ advances the concurrency
  // base), DELETE removes the row + bounces the user off a record deleted out
  // from under them. Case list + activity timeline are handled in their own
  // components (CaseListScreen / ClientSearchView). Replaces the retired
  // client-driven list-update relay.
  useEntityChangeSync()
  // (D12) Other people's changes → the change buses (every screen re-reads),
  // catch-up after a reconnect / the tab coming back, stamp polling while the
  // socket is down.
  useLiveTopics()
  // ── Post-login branded splash (LoadingSplash) ───────────────
  // Shows AFTER sign-in while the app loads into the dashboard, then
  // fades out. Initialised to isAuthenticated so a SESSION RESTORE on
  // refresh (token already in localStorage → authenticated on mount)
  // shows it from render #1 with no dashboard flash; a fresh login flips
  // it on via the effect below. It renders ONLY inside the authenticated
  // layout, so it never appears on the login screen. `splashFading`
  // drives the CSS opacity fade; `splashHandledRef` makes it a
  // once-per-app-load thing (normal navigation never re-triggers it).
  const [showSplash, setShowSplash] = useState(isAuthenticated)
  const [splashFading, setSplashFading] = useState(false)
  const splashHandledRef = useRef(false)
  useEffect(() => {
    // Reset when signed out so a later sign-in shows the splash again.
    if (!isAuthenticated) {
      splashHandledRef.current = false
      setShowSplash(false)
      setSplashFading(false)
      return
    }
    // Once per authenticated app-load only (guards against re-showing on
    // navigation — though nav never flips isAuthenticated anyway).
    if (splashHandledRef.current) return
    splashHandledRef.current = true
    setShowSplash(true)
    setSplashFading(false)
    // Minimum on-screen time so it reads as a deliberate branded moment,
    // not a flash-and-vanish on a fast load; then a CSS opacity fade. The
    // dashboard mounts + fetches UNDERNEATH during this window (and has
    // its own skeletons if its data isn't ready when the splash clears).
    const MIN_MS = 1100
    const FADE_MS = 500
    const fadeTimer = window.setTimeout(() => setSplashFading(true), MIN_MS)
    const doneTimer = window.setTimeout(() => setShowSplash(false), MIN_MS + FADE_MS)
    return () => { window.clearTimeout(fadeTimer); window.clearTimeout(doneTimer) }
  }, [isAuthenticated])
  // Synchronous deep-link detection so render #1 already shows the accept
  // screen — no flash of authenticated UI when a logged-in user clicks an
  // invite/reset link. Pure read of window.location, no side effects,
  // StrictMode-safe. The useEffect below still runs (clearAuth + token
  // stash) for the rest of the flow; this only fixes the first paint.
  //
  // ★ ONE-SHOT (invite-accept regression fix): this used to be re-derived from
  // window.location on EVERY render, and the ?token= was never removed from
  // the URL — so the deep-link override (`!isAuthenticated || deepLink` below)
  // could never be exited: after a SUCCESSFUL accept (account created, setAuth
  // called) the accept form stayed on screen, a second click re-submitted the
  // now-used token ("already been accepted"), and "Back to login" only set
  // authView, which the override ignored. Now it's captured ONCE at mount into
  // state and cleared by leaveDeepLink() (which also strips ?token= from the
  // URL so a refresh can't re-enter the used link).
  const [deepLink, setDeepLink] = useState<{ view: 'acceptInvite' | 'acceptReset'; token: string } | null>(() => {
    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) return null
    return {
      view: window.location.pathname.startsWith('/accept-invite') ? 'acceptInvite' : 'acceptReset',
      token,
    }
  })
  // Exit the invite/reset deep link: drop the override, strip the URL, and
  // (optionally) land on an auth screen. Called on "Back to login", after a
  // successful reset, and after a successful invite accept (setAuth already
  // ran, so dropping the override reveals the authenticated app).
  const leaveDeepLink = (next?: 'login' | 'requestReset') => {
    setDeepLink(null)
    if (new URLSearchParams(window.location.search).has('token')) {
      window.history.replaceState({}, '', '/')
    }
    if (next) setAuthView(next)
  }
  // Deep-link parser — runs ONCE on mount, BEFORE the getMeApi refresh
  // below. If the URL has a ?token=… query param (i.e. the user clicked
  // a password-reset link OR a team-invite link in their email), flip
  // authView to the matching screen and stash the token in the store for
  // it to consume. Branches on pathname: /accept-invite → acceptInvite,
  // anything else (including the default /accept-reset) → acceptReset.
  // No token → leave authView at its default 'login'.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (token) {
      // If the user is already logged in on this device, log them out first.
      // They clicked a deep link — their intent is to act on that link
      // (set a new password, or accept an invite), not continue their
      // existing session. Without this, the ?token= would be detected but
      // the `if (!isAuthenticated)` branch below would skip the auth-screen
      // case entirely and render the case list.
      useCasevoStore.getState().clearAuth()
      if (window.location.pathname.startsWith('/accept-invite')) {
        setAuthView('acceptInvite')
        setPendingInviteToken(token)
      } else {
        setAuthView('acceptReset')
        setPendingResetToken(token)
      }
    } else {
      // "Open in new tab" deep-link: ?caseId=<id>. Mutually exclusive with
      // ?token above (a tab has one or the other), so this only runs when
      // there's no invite/reset token. We STASH the id (don't open here —
      // the open must wait until auth is confirmed so fetchCaseById is
      // authenticated) and strip the param via replaceState so a refresh /
      // bookmark doesn't re-trigger. The consume effect below opens it once
      // authenticated. Auth carries into the new tab via the shared
      // localStorage bearer token.
      const caseId = params.get('caseId')
      if (caseId) {
        setPendingCaseId(caseId)
        // A bill-approval email adds &billId= → land on the case's Billing
        // section with that bill open (CaseBillingSection consumes pendingBill).
        const billId = params.get('billId')
        if (billId) {
          useCasevoStore.getState().setPendingBill({ billId, caseId })
          useCasevoStore.getState().setCaseInitialSection('billing')
        } else if (params.get('section')) {
          // A refresh on a case section (?caseId=…&section=…) lands back on it
          // (ViewingPane only honours a section that's visible on this case).
          useCasevoStore.setState({ caseSectionMemo: { caseId, section: params.get('section') as CaseSectionKey } })
        }
        // (D12) An e-signature email: &section=settlement&statementId=… → the
        // Settlement section, scrolled to that statement (StatementPanel takes it).
        const statementId = params.get('statementId')
        if (statementId && !billId) {
          useCasevoStore.getState().setCaseInitialSection('settlement')
          requestStatementFocus({ caseId, statementId })
        }
        // Keep the entry's state (its depth / place) — only the URL changes;
        // the place sync re-writes it once the case is on screen.
        window.history.replaceState(window.history.state, '', window.location.pathname)
      } else {
        // "Open in new tab" deep-link: ?taskId=<id> (e.g. an email's
        // "View task" CTA). Mirrors ?caseId, and is mutually exclusive
        // with it (a given email link carries one or the other). We stash
        // the id into pendingTaskId — the store slot TasksView already
        // consumes on mount to OPEN a task — AND record it in
        // coldOpenTaskIdRef so the consume effect below navigates to the
        // Tasks view for THIS URL cold-open only (not for in-app producers
        // that also set pendingTaskId). Strip the param via replaceState
        // so a refresh / bookmark doesn't re-trigger.
        const taskId = params.get('taskId')
        if (taskId) {
          coldOpenTaskIdRef.current = taskId
          useCasevoStore.getState().setPendingTaskId(taskId)
          window.history.replaceState(window.history.state, '', window.location.pathname)
        } else {
          // "Open in new tab" deep-link: ?day=YYYY-MM-DD (e.g. the reminder
          // email's "Open Casevo" CTA). Mirrors ?caseId/?taskId — stash the
          // day + record it in coldOpenDayRef so the consume effect opens the
          // Calendar for THIS URL cold-open only, then strip the param.
          // Validated as YYYY-MM-DD; anything else is ignored.
          const day = params.get('day')
          if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) {
            coldOpenDayRef.current = day
            useCasevoStore.getState().setPendingCalendarDate(day)
            window.history.replaceState(window.history.state, '', window.location.pathname)
          } else {
            // Any other place in the URL (?client=…&tab=…, ?view=billing&billId=…,
            // ?view=admin&section=…) — a refresh, or a link copied from the
            // address bar. A refresh also has the entry's stored place (same
            // URL) — prefer it, it's exact. The consume effect opens it.
            const stored = (window.history.state as { place?: Place } | null)?.place
            const search = window.location.search
            coldOpenPlaceRef.current = stored && placeToSearch(stored) === search
              ? stored
              : placeFromSearch(search)
                // The Microsoft OAuth return lands on /settings?ms=… → Settings ›
                // Email & Microsoft (MicrosoftPanel shows the result banner).
                ?? (window.location.pathname.startsWith('/settings') ? { view: 'settings', params: { section: 'email' } } : null)
          }
        }
      }
    }
    // Run once on mount — the dependency list is intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Consume a stashed ?caseId once authenticated → open that case directly.
  // Keyed on isAuthenticated so it fires both when the tab loads already
  // logged in (localStorage token present at mount) AND after a fresh login
  // (isAuthenticated flips true). The bearer token is read from localStorage
  // by the API interceptor, so the fetch is authenticated even before the
  // getMeApi refresh resolves. A bad / cross-firm id 404s → we swallow it and
  // fall through to the default view (dashboard) rather than crashing.
  useEffect(() => {
    if (!isAuthenticated || !pendingCaseId) return
    const id = pendingCaseId
    setPendingCaseId(null) // consume once
    // Drop the default landing view FIRST: a fresh tab loads with
    // showDashboard=true, and renderMain checks showDashboard before falling
    // through to <ViewingPane/>, so setting currentCase alone leaves the
    // dashboard showing. Clearing it lets ViewingPane render the case once the
    // fetch resolves (and the case list — not the dashboard — if the id is bad).
    useCasevoStore.getState().setShowDashboard(false)
    setOpeningCaseById(true)
    // onShown: release the loading branch as soon as the CASE row is mapped —
    // the satellites (witnesses/deadlines/TPI/files) keep loading behind it.
    useCasevoStore.getState().setCurrentCaseById(id, undefined, () => setOpeningCaseById(false))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[App] open-in-new-tab caseId failed', err)
      })
      // Cleared on BOTH success (currentCase now set → ViewingPane shows the
      // case) and error (falls through to the case list). Either way the
      // spinner ends without a case-list flash in between.
      .finally(() => setOpeningCaseById(false))
  }, [isAuthenticated, pendingCaseId, setPendingCaseId])
  // Consume a stashed ?taskId cold-open once authenticated → land on the
  // Tasks view, where TasksView's own mount effect reads pendingTaskId and
  // opens that task. Gated by coldOpenTaskIdRef so this fires ONLY for the
  // URL deep-link, never for the in-app cross-view sets of pendingTaskId
  // (those producers navigate themselves). Keyed on isAuthenticated so it
  // fires both when the tab loads already logged in AND after a fresh
  // login. We leave pendingTaskId set — TasksView consumes it — and just
  // navigate via the standard takeover pattern (closeAllTakeovers +
  // setShowTasks), exactly Sidebar.handleTasks / DashboardView.
  // closeAllTakeovers preserves pendingTaskId (it only zeroes the show*
  // flags + currentCase). A bad / stale / cross-firm taskId simply won't
  // match any row in TasksView → the user lands on the Tasks list (no
  // crash), the same graceful fallback as the ?caseId cold-open.
  useEffect(() => {
    if (!isAuthenticated) return
    const id = coldOpenTaskIdRef.current
    if (!id) return
    coldOpenTaskIdRef.current = null // consume once
    useCasevoStore.getState().closeAllTakeovers()
    useCasevoStore.getState().setShowTasks(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])
  // Consume a stashed ?day cold-open once authenticated → open the
  // Calendar. pendingCalendarDate is left set (closeAllTakeovers preserves
  // it); CalendarView's mount effect reads it to switch to Day view,
  // anchor on that day + open the day panel, then clears it. Gated by
  // coldOpenDayRef so this fires only for the URL deep-link.
  useEffect(() => {
    if (!isAuthenticated) return
    if (!coldOpenDayRef.current) return
    coldOpenDayRef.current = null // consume once
    useCasevoStore.getState().closeAllTakeovers()
    useCasevoStore.getState().setShowCalendar(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])
  // Consume any other URL place once authenticated (navigation/placeUrl) —
  // the same restore Back/Forward uses, so it lands on exactly that place.
  // Fresh load: no history entry is added. Ids go through the normal
  // access-checked loads, so a bad / cross-firm id falls back (client →
  // the client search, case → the case list, unknown bill → the list).
  useEffect(() => {
    if (!isAuthenticated) return
    const target = coldOpenPlaceRef.current
    if (!target) return
    coldOpenPlaceRef.current = null // consume once
    const store = useCasevoStore.getState()
    store.setIsRestoringView(true)
    void store.restoreView(target).finally(() => useCasevoStore.getState().setIsRestoringView(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])
  // Keep the current history entry's place + the address bar in step with
  // what's on screen (navigation/navigate.ts). Declared AFTER the cold-open
  // consumers so they read the URL before it is first re-written.
  useEffect(() => {
    if (!isAuthenticated) return
    return startPlaceSync()
  }, [isAuthenticated])
  // Browser Back / Forward. Each history entry carries its own place
  // (navigation/navigate.ts), so the handler restores exactly the entry the
  // browser moved to — Back and Forward alike, also after a refresh.
  //
  // Guards first — the browser has ALREADY moved, so "stay" steps it back
  // the other way (and skips the popstate that step fires):
  //   • timer nav guard: an open timer modal with unsaved time shows its
  //     "timer running" popup (Save / Discard → continue; Keep timing → stay);
  //   • unsaved-changes guard: an explicit-save screen with edits shows
  //     "You have unsaved changes" (Discard → continue; Stay → stay).
  // While a guard is open the place sync is paused, so the target entry is
  // never stamped with the screen we're still on.
  useEffect(() => {
    if (!isAuthenticated) return
    let skipNextPop = false
    const handlePopState = (event: PopStateEvent): void => {
      if (skipNextPop) { skipNextPop = false; return }
      const st = event.state as { casevoSeq?: unknown; place?: Place } | null
      const targetSeq = st && typeof st.casevoSeq === 'number' ? st.casevoSeq : 0
      const wentForward = targetSeq > useCasevoStore.getState().currentSeq
      const stay = (): void => {
        pausePlaceSync(false)
        skipNextPop = true
        window.history.go(wentForward ? -1 : 1)
      }
      const proceed = async (): Promise<void> => {
        if (!(await confirmLeave())) { stay(); return }
        await runPop(st?.place ?? null, targetSeq)
      }
      pausePlaceSync(true)
      const timerGuard = getTimerNavGuard()
      if (timerGuard) { timerGuard(() => { void proceed() }, stay); return }
      void proceed()
    }
    const runPop = async (place: Place | null, targetSeq: number): Promise<void> => {
      // Drain every pending autosave BEFORE the view changes, against the
      // still-current view (so the last keystroke is never lost on Back).
      await flushAllAutoSaves()
      const store = useCasevoStore.getState()
      // An entry without a stored place (made before this app session, or
      // by something else) → its URL; else the landing view.
      const target = place ?? placeFromSearch(window.location.search) ?? { view: 'dashboard' as const }
      store.setIsRestoringView(true)
      pausePlaceSync(false)
      try {
        // Record the entry we're now on so the NEXT popstate compares
        // against the right seq.
        store.setCurrentSeq(targetSeq)
        // caseDetail restore goes through setCurrentCaseById → isCaseLoading
        // → ViewingPane shows its skeleton (never a case-list flash).
        await store.restoreView(target)
      } finally {
        useCasevoStore.getState().setIsRestoringView(false)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [isAuthenticated])
  // The app theme (Settings → Appearance): apply the cached choice, switch
  // live at UK sunset / sunrise (Automatic) or with the device (Match my
  // device), re-checking on tab focus / wake. Runs signed in or out.
  useEffect(() => startThemeController(), [])
  // On mount, refresh the cached user from the backend. This fires
  // setAuthUser, which re-seeds viewingPaneToggles from the user's
  // saved preferences. Without this, page refresh hydrates from
  // stale localStorage and toggles reset to defaults.
  // Failure (network, expired token) is silently logged — we keep
  // the stored user rather than logging the user out on a blip.
  useEffect(() => {
    if (!isAuthenticated) return
    getMeApi()
      .then((user) => setAuthUser(user))
      .catch((err) => console.error('[App] getMeApi failed', err))
    // Run once on mount — the dependency list is intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // ★ LIVE PERMISSIONS. The cached user (role + effectivePermissions) used to
  // refresh only on page load, so an admin granting/revoking a permission
  // didn't show until the user refreshed (the server already enforced it).
  // Now we re-fetch /api/auth/me when:
  //   • the server pushes 'permissions-changed' over the presence socket
  //     (admin changed this user's overrides / role / the firm defaults), or
  //     the socket RE-connects (a push may have been missed while offline);
  //   • the tab becomes visible again after 60s+ (fallback if the socket is
  //     unavailable).
  // setAuthUser bumps store.permissionsVersion when role/permissions really
  // changed, so permission-scoped screens drop their caches and refetch; the
  // gated controls (Team/Firm toggles, calendar Firm view...) read the store
  // and re-render immediately. Debounced + single-flight: a burst = one fetch.
  useEffect(() => {
    if (!isAuthenticated) return
    let timer: number | undefined
    let inFlight = false
    let lastAt = Date.now()
    const refresh = (): void => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        if (inFlight) return
        inFlight = true
        lastAt = Date.now()
        getMeApi()
          .then((user) => { if (useCasevoStore.getState().isAuthenticated) setAuthUser(user) })
          .catch((err) => console.error('[App] permissions refresh failed', err))
          .finally(() => { inFlight = false })
        // Same trigger (incl. every socket RE-connect): re-read the firm
        // members too, so a rename broadcast missed while offline lands.
        void useCasevoStore.getState().loadFirmUsers(true)
        // …and the firm's section-visibility setting (case History): an admin
        // changing it pushes the same event, so the section shows / hides live.
        getCaseHistoryVisibilityApi()
          .then((v) => useCasevoStore.getState().setCaseHistoryVisibility(v))
          .catch(() => { /* keep the current value; the server still gates History */ })
      }, 250)
    }
    const unsubscribe = presence.subscribePermissionsChanged(refresh)
    // ★ Live identity: a firm member renamed themselves / changed avatar →
    // patch the central firmUsers map (+ authUser if it's us in another tab).
    // Everything that renders people by id re-renders with the new name.
    const unsubscribeUser = presence.subscribeUserUpdated((u) => {
      useCasevoStore.getState().applyUserDisplayUpdate(u)
    })
    // The firm-members map is the single live source for names — load it
    // once per session for everyone (GET /api/users is open to members).
    void useCasevoStore.getState().loadFirmUsers()
    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && Date.now() - lastAt > 60_000) refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      unsubscribe()
      unsubscribeUser()
      document.removeEventListener('visibilitychange', onVisible)
      window.clearTimeout(timer)
    }
  }, [isAuthenticated, setAuthUser])
  // Load the firm + user colour schemes into the store so TimelineRow's
  // colour cascade has them cached. Keyed on isAuthenticated (not mount-
  // only) so it fires on BOTH page reload (authenticated at mount) AND a
  // fresh login (isAuthenticated flips true). Failure is swallowed in the
  // store (leaves {} → shipped defaults render — no visible breakage).
  useEffect(() => {
    if (!isAuthenticated) return
    void loadColourSchemes()
    // Favourites ride the same auth-keyed load (fires on reload + fresh
    // login). Failure is swallowed in the store — the star just reads as
    // "not favourited" until a later successful load.
    void loadFavourites()
  }, [isAuthenticated, loadColourSchemes, loadFavourites])
  // Auto save only runs when authenticated. useAutoSave handles
  // the Case row; useClientAutoSave handles the linked Client
  // record (Phase 4). Both are singletons — flush helpers live as
  // module-scope plain functions imported by nav handlers.
  useAutoSave()
  useClientAutoSave()
  // Callback-ref state for the full-width topbar container (Layout B). AppHeader
  // reads this via HeaderPortalContext and portals its nav into it. Declared here
  // with the other hooks — above the auth/2FA early returns — so the hook order
  // is stable on every render (rules-of-hooks).
  const [topbarEl, setTopbarEl] = useState<HTMLElement | null>(null)
  // Sidebar collapse state — same store-backed toggle the Sidebar reads
  // (viewingPaneToggles['sidebar.collapsed']). Consumed here so the DIVIDER
  // GRIP on the sidebar/content seam (below) can toggle it and track the
  // sidebar's edge position (208px expanded / 64px collapsed).
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedToggle('sidebar.collapsed', false)
  // Unauthenticated branch — pick which auth screen to render based
  // on authView. Replaces the previous local `showRegister` boolean
  // so the Forgot Password flow can navigate between five distinct
  // screens (login / register / requestReset / acceptReset /
  // confirmation), and the URL deep-link parser above can land the
  // user directly on Accept Reset.
  if (!isAuthenticated || deepLink) {
    switch (deepLink ? deepLink.view : authView) {
      case 'register':
        return <RegisterScreen onShowLogin={() => setAuthView('login')} />
      case 'requestReset':
        return (
          <RequestResetScreen
            onShowLogin={() => setAuthView('login')}
            onSuccess={() => setAuthView('confirmation')}
          />
        )
      case 'acceptReset':
        return (
          <AcceptResetScreen
            token={deepLink?.token ?? pendingResetToken ?? ''}
            onShowLogin={() => leaveDeepLink('login')}
            onShowRequestReset={() => leaveDeepLink('requestReset')}
          />
        )
      case 'acceptInvite':
        return (
          <AcceptInviteScreen
            token={deepLink?.token ?? pendingInviteToken ?? ''}
            onShowLogin={() => leaveDeepLink('login')}
            onAccepted={() => leaveDeepLink()}
          />
        )
      case 'confirmation':
        return <ConfirmationScreen onShowLogin={() => setAuthView('login')} />
      case 'login':
      default:
        // onShowRegister dropped — Casevo is invite-only (24 June);
        // LoginScreen no longer surfaces a register link. The
        // 'register' case above is kept intact so stale bookmarks
        // / old email links still resolve, but they now land on
        // an invite-only notice, not the dead form.
        return (
          <LoginScreen
            onShowRequestReset={() => setAuthView('requestReset')}
          />
        )
    }
  }
  // ── FORCED 2FA ENFORCEMENT GATE ──────────────────────────────
  // Past this point the user is authenticated. If their firm requires
  // 2FA (firmRequire2fa) and they don't yet have it enabled
  // (!twoFactorEnabled), divert to the mandatory setup screen INSTEAD of
  // the app — no sidebar, no skip, the only exit is completing enrolment
  // (which refreshes getMe → twoFactorEnabled true → this condition
  // falls false → the app renders below).
  //
  // Strict `=== true` / `!== true` reads: both flags are optional on
  // AuthUser and are undefined on the partial user login returns before
  // the getMe hydration lands. Treating undefined as "don't gate" means:
  //   - a non-enforcing firm (firmRequire2fa falsy) NEVER sees the gate;
  //   - we never lock someone out on incomplete data — the gate only
  //     appears once getMe has confirmed firmRequire2fa===true AND the
  //     user genuinely lacks 2FA.
  // A mid-enrolment user (row exists, enabled:false) reports
  // twoFactorEnabled:false, so they stay gated until they finish. An
  // admin who just enabled enforcement hits this on their next getMe and
  // enrols with their still-valid session — no lockout.
  if (authUser && authUser.firmRequire2fa === true && authUser.twoFactorEnabled !== true) {
    return <ForcedTwoFactorSetup />
  }
  // Authenticated layout — sidebar on the left, screen content on
  // the right. The flex container is `md:flex` so on mobile the
  // sidebar (fixed-positioned overlay) sits outside the flex flow
  // and the main element fills the viewport on its own. On desktop
  // the sidebar is a flex sibling that takes 60/280px and the main
  // element flexes to fill the rest. min-w-0 on <main> stops its
  // children's max-width: max-content from breaking the flex shrink.
  // Main-area resolution order:
  //   0. showDashboard      → DashboardView       (Stage B landing view)
  //   1. showTasks          → TasksView           (sidebar Phase 2f)
  //   2. showDocumentSearch → DocumentSearchView  (sidebar Phase 2d)
  //   3. showCalendar       → CalendarView        (sidebar Phase 2c)
  //   4. showClientSearch   → ClientSearchView    (sidebar Phase 2b)
  //   5. showSettings       → Settings            (sidebar account section)
  //   6. otherwise          → ViewingPane (which itself swaps in
  //                           CaseListScreen when currentCase is null)
  // Dashboard is at position 0 = highest precedence. When true it
  // wins over every other flag, matching "Dashboard is the app root".
  // The sidebar's handlers enforce mutual exclusion via
  // closeAllTakeovers() (Stage B refactor of the old per-handler
  // "close the other N" pattern), so in practice only one flag is
  // ever true at a time. The precedence order above is the tiebreaker
  // if a race ever puts multiple flags true momentarily — most
  // recently added zone wins. Closing a zone via ← Back falls through
  // to whichever flag is still true, so the user lands back on their
  // previous screen.
  const renderMain = (): React.ReactNode => {
    // Cold-open (?caseId new tab): loading shell while the case fetch is in
    // flight, so we go skeleton → case, never flashing the case list.
    // ViewingPane in its loading state keeps the page shell (topbar header +
    // 1180 column) and skeletons only the content — the topbar never empties.
    // Once currentCase lands, the same instance renders the real case page.
    if (openingCaseById) return <ViewingPane forceLoading />
    if (showDashboard) return <DashboardView />
    if (showTasks) {
      // A task opened FROM the calendar renders as an OVERLAY over the still-
      // mounted calendar (CalendarView.handleOpenTask no longer unmounts it).
      // App renders BOTH: the calendar as the base, TasksView on top drawing
      // ONLY its fixed modal overlay (its page chrome is suppressed while
      // openedFromCalendar), so the calendar shows dimmed through the backdrop
      // — mirroring AppointmentFormModal. This also preserves the calendar's
      // anchor day + focus theme and avoids a re-fetch on return.
      if (showCalendar && taskOpenedFrom === 'calendar') {
        return (
          <>
            <CalendarView />
            <TasksView />
          </>
        )
      }
      return <TasksView />
    }
    if (showDocumentSearch) return <DocumentSearchView />
    // Invoicing Stage 1 — firm Billing › Bills (same precedence slot as snapshotCurrentView).
    if (showBilling) return <BillingView />
    if (showCalendar) return <CalendarView />
    if (showClientSearch) return <ClientSearchView />
    if (showAdmin) return <AdminPage />
    if (showSettings) return <Settings />
    return <ViewingPane />
  }
  // Layout B — full-width TOPBAR across the very top (above BOTH the Sidebar and
  // the content), then a row below it: Sidebar (left) + content (right). Each
  // per-screen AppHeader portals its <nav> into the topbar container via
  // HeaderPortalContext, so the header spans full width without hoisting every
  // screen's header props (back button, wordmark handler, action buttons). The
  // row's deep-forest bg shows ONLY in the content pane's rounded top-left
  // corner — the soft "crook" of the L, matching the mockup.
  return (
    <HeaderPortalContext.Provider value={topbarEl}>
      <div className="bg-casevo-offwhite md:flex md:flex-col md:h-screen md:overflow-hidden">
        {/* Full-width topbar container — AppHeader portals its nav here. Sticky on
            mobile (the page itself scrolls); inert on desktop (the column has a
            fixed height and the content scrolls internally instead).
            z-40 keeps it (and its shadow) above the sidebar (z-30) and the
            content (z-0). Desktop-only DOWNWARD drop shadow so the raised
            chrome casts a soft shadow onto the content's top edge — same weight
            as the case cards (light 0 6 18 rgba(10,25,17,.22); dark tightened).
            The "raised chrome" look is NOT cast by the topbar/sidebar anymore
            (that stacked shadow-on-shadow at the L junction in dark mode); it now
            comes from the CONTENT pane being recessed — an inset shadow along its
            full top + left edges (see <main> below). So the topbar + sidebar are
            pure forest with NO shadow between them → one seamless L. */}
        <div ref={setTopbarEl} className="sticky top-0 z-40 shrink-0" />
        {/* Row below the topbar: Sidebar + content. Deep-forest bg so the content
            pane's rounded top-left corner reveals forest = the L's inner crook. */}
        <div className="md:relative md:flex md:flex-1 md:min-h-0 bg-casevo-header">
          <Sidebar />
          <main className="flex-1 min-w-0 bg-casevo-offwhite md:overflow-y-auto md:overflow-x-hidden md:rounded-tl-3xl">
            {/* Accessibility screen-zoom + edge-pan wraps ONLY the main content.
                Inert at 100% (transform:none), so default behaviour is unchanged. */}
            {/* Admin-only: the server's scheduled alert jobs aren't running. */}
            <AlertsStatusBanner />
            <ScreenZoom>{renderMain()}</ScreenZoom>
          </main>
          {/* CONTENT DEPTH OVERLAY — the "chrome raised above content" shadow.
              This is an EMPTY sibling of <main> (not inside it) so the depth stays
              pinned as the content scrolls, positioned over ONLY the content area —
              left tracks the sidebar edge (208/64) so it can NEVER touch the chrome
              → the seamless L is preserved. The depth is TWO inset box-shadows (top
              edge + left edge). Because inset shadows follow the element's
              border-radius, the rounded-tl-3xl makes the shadow CURVE around the
              crook corner instead of squaring off (the old two-straight-gradients
              met at a hard 90°). They're on an empty div (nothing paints over them)
              so they read reliably — unlike an inset on the scrolling <main>, where
              content covered them. Soft + subtle (low alpha, tight blur/spread so
              it hugs the very edge). pointer-events-none; z-10 (above content, below
              the z-40 seam handle). Desktop-only; mobile unaffected. */}
          <div
            aria-hidden="true"
            className={`hidden md:block pointer-events-none absolute top-0 bottom-0 right-0 z-10
                        rounded-tl-3xl transition-[left] duration-150
                        shadow-[inset_0_9px_9px_-6px_rgba(0,0,0,0.24),inset_9px_0_9px_-6px_rgba(0,0,0,0.24)]
                        dark:shadow-[inset_0_9px_9px_-6px_rgba(0,0,0,0.30),inset_9px_0_9px_-6px_rgba(0,0,0,0.30)]
                        ${sidebarCollapsed ? 'left-[64px]' : 'left-[208px]'}`}
          />
          {/* SMART SEAM HANDLE (collapse control) — desktop-only. A generous
              ~34px invisible HIT-ZONE centred on the seam between the sidebar
              and the content, anchored to the row (md:relative above) at the
              sidebar's right edge so it tracks the width (left-[208px] expanded
              / left-[64px] collapsed) and animates with it (transition-[left]).
              The whole strip is the click target — forgiving to hit. Inside it,
              the VISIBLE HANDLE is AT REST a tiny faint pill (5×34, bg-white/10)
              — clean, barely there; ON HOVER (or keyboard focus) of the strip it
              GROWS to a 22×54 tab with the deep-forest bg + subtle border +
              shadow and the chevron fades in (easy, obvious target). Chevron
              rotates 180° when collapsed. Click → toggles sidebar.collapsed.
              z-40 keeps it above the sidebar (z-30) + content. Hidden on mobile
              (the sidebar is a slide-in drawer, dismissed by its own close X). */}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={sidebarCollapsed}
            className={`hidden md:flex group/seam absolute top-0 bottom-0 z-40 w-[34px] -translate-x-1/2
                        items-center justify-center bg-transparent border-0 p-0 cursor-pointer
                        transition-[left] duration-150 focus:outline-none casevo-focus
                        ${sidebarCollapsed ? 'left-[64px]' : 'left-[208px]'}`}
          >
            <span
              className="flex items-center justify-center border border-transparent
                         w-[5px] h-[34px] rounded-full bg-[#9aa7ac] dark:bg-[#33453b]
                         shadow-[0_1px_2px_rgba(0,0,0,0.2)]
                         group-hover/seam:w-[22px] group-hover/seam:h-[54px] group-hover/seam:rounded-lg
                         group-hover/seam:bg-casevo-header dark:group-hover/seam:bg-casevo-header
                         group-hover/seam:border-white/15 group-hover/seam:shadow-[0_2px_10px_rgba(0,0,0,0.35)]
                         group-focus-visible/seam:w-[22px] group-focus-visible/seam:h-[54px]
                         group-focus-visible/seam:rounded-lg group-focus-visible/seam:bg-casevo-header
                         dark:group-focus-visible/seam:bg-casevo-header
                         group-focus-visible/seam:border-white/15
                         group-focus-visible/seam:shadow-[0_2px_10px_rgba(0,0,0,0.35)]
                         transition-all duration-150 ease-out"
            >
              <svg
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                className={`text-casevo-light opacity-0 group-hover/seam:opacity-100
                            group-focus-visible/seam:opacity-100 transition-[opacity,transform] duration-150
                            ${sidebarCollapsed ? 'rotate-180' : ''}`}
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </span>
          </button>
        </div>
        {/* Global favourites error toast (e.g. the 20-item cap). Fixed overlay so
            it shows regardless of which surface the star was clicked from. */}
        <FavouritesErrorToast />
        <SaveConflictToast />
        {/* The shared "unsaved changes — leave without saving?" confirm. */}
        <UnsavedChangesDialog />
        {/* Branded post-login splash — covers the dashboard while the app loads
            in, then fades out. Only rendered here (authenticated layout). */}
        {showSplash && <LoadingSplash fading={splashFading} />}
      </div>
    </HeaderPortalContext.Provider>
  )
}
export default App
